import { Network } from '@capacitor/network'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { configApi, errorMessagesApi } from '@/api/client'
import { useNotifications } from '@/composables/useNotifications'
import jobs from '@/jobs'
import { router } from '@/router'
import { useAuthStore } from '@/store/auth'
import { useBeepersStore } from '@/store/beepers'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useCouriersStore } from '@/store/couriers'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import mqtt from '@/sync/mqtt'
import sync from '@/sync/service'

type ErrorMessage = {
  id: string
  title: string
  description: string
}

export const useStatusStore = defineStore('status', () => {
  const { notifyError, close } = useNotifications()

  const networkConnected = ref(false)
  const mqttConnected = ref(false)
  const errorMessages = ref<ErrorMessage[]>([])
  const hasTimeError = ref(false)
  const initialLoadCompleted = ref(false)

  Network.getStatus().then(status => {
    networkConnected.value = status.connected
  })

  Network.addListener('networkStatusChange', status => {
    networkConnected.value = status.connected
  })

  const isConnected = computed(() => {
    return mqttConnected.value && networkConnected.value
  })

  async function checkDeviceStatus() {
    mqttConnected.value = mqtt.isConnected()
    if (networkConnected.value) {
      await checkDeviceTime()
    }
  }

  async function checkDeviceTime() {
    try {
      const { time: serverTime } = await configApi.getTime()
      const timeDifference = Math.abs(new Date(serverTime).getTime() - new Date().getTime())
      hasTimeError.value = timeDifference > 5 * 60 * 1000
    } catch {
      hasTimeError.value = false
    }
  }

  function updateMqttStatus(connected: boolean) {
    mqttConnected.value = connected
  }

  async function refreshErrorMessages(inputErrorMessages?: ErrorMessage[]) {
    const newErrorMessages = inputErrorMessages
      ? inputErrorMessages
      : await errorMessagesApi.getErrorMessages()

    const oldErrorIds = new Set(errorMessages.value.map(msg => msg.id))
    const newErrorIds = new Set(newErrorMessages.map(msg => msg.id))

    oldErrorIds.forEach(oldId => {
      if (!newErrorIds.has(oldId)) {
        close(`error-${oldId}`)
      }
    })

    newErrorMessages.forEach((errorMessage: ErrorMessage) => {
      if (!oldErrorIds.has(errorMessage.id)) {
        notifyError({
          id: `error-${errorMessage.id}`,
          title: errorMessage.title,
          description: errorMessage.description,
          important: true
        })
      }
    })

    errorMessages.value = newErrorMessages
  }

  async function performInitialSync() {
    const authStore = useAuthStore()
    const catalogStore = useCatalogStore()
    const configStore = useConfigStore()
    const promotionsStore = usePromotionsStore()
    const couriersStore = useCouriersStore()
    const beepersStore = useBeepersStore()
    const floorplanStore = useFloorplanStore()

    jobs.start()

    if (!authStore.isAuthenticated) return

    await Promise.allSettled([
      sync.resync(),
      checkDeviceStatus(),
      refreshErrorMessages(),
      authStore.refreshCurrentLocation(),
      floorplanStore.refreshFloorplans(),
      catalogStore.refreshCatalogs(),
      configStore.refreshConfig(),
      authStore.refreshBillingStatus(),
      authStore.refreshEmployees(),
      promotionsStore.refreshPromotions(),
      couriersStore.refreshCouriers(),
      beepersStore.refreshBeepers()
    ])

    initialLoadCompleted.value = true
  }

  watch(hasTimeError, () => {
    if (hasTimeError.value) {
      router.push({ name: 'wrongTime' })
    } else {
      router.push({ name: 'employees' })
    }
  })

  return {
    networkConnected,
    mqttConnected,
    errorMessages,
    hasTimeError,
    initialLoadCompleted,
    isConnected,

    checkDeviceStatus,
    updateMqttStatus,
    refreshErrorMessages,
    performInitialSync
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStatusStore, import.meta.hot))
}
